import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { WemapDrawer } from '../../../design-system/components/wemap/WemapDrawer';
import {
    Destination,
    DestinationTypeRegion,
} from '../../../resources/destination/domain/types/destination';
import { PointOfInterest } from '../../../resources/pointOfInterest/domain/types/PointOfInterest';
import { Region } from '../../../resources/region/domain/types/region';
import { useUrlGenerator } from '../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

export function WhereToGoSectionMapDrawer({
    onClose,
    selectedItem,
}: {
    onClose: () => void;
    selectedItem:
        | {
              type: 'destinationRegion';
              item: PickDomain<
                  DestinationTypeRegion,
                  'id' | 'name' | 'coverPicture' | 'slug',
                  {
                      description: DestinationTypeRegion['description'] | null;
                  }
              >;
          }
        | {
              type: 'region';
              item: PickDomain<
                  Region,
                  'id' | 'name' | 'description' | 'slug',
                  {
                      coverPicture: Region['coverPicture'] | null;
                      destination: PickDomain<Region['destination'], 'id' | 'slug'>;
                  }
              >;
          }
        | {
              type: 'pointOfInterest';
              item: PickDomain<
                  PointOfInterest,
                  'name' | 'slug' | 'id',
                  {
                      averageRating: PointOfInterest['averageRating'] | null;
                      description: PointOfInterest['description'] | null;
                      picture: PointOfInterest['picture'] | null;
                      destination: PickDomain<Destination, 'slug'>;
                  }
              >;
          };
}) {
    const { formatMessage } = useIntl();
    const { generateUrl } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    const selectedDestinationRegion = selectedItem.type === 'destinationRegion';
    const selectedRegion = selectedItem.type === 'region';
    const selectedPointOfInterest = selectedItem.type === 'pointOfInterest';

    function handleClose() {
        onClose();
    }

    return (
        <>
            {selectedDestinationRegion ? (
                <WemapDrawer
                    title={selectedItem.item.name}
                    description={selectedItem.item.description}
                    averageRating={null}
                    picture={selectedItem.item.coverPicture}
                    cta={{
                        label: formatMessage({
                            id: 'global.cta.discoverRegion',
                        }),
                        url: generateUrl('destination', {
                            destinationSlug: selectedItem.item.slug,
                        }),
                    }}
                    onClose={handleClose}
                    onClickCta={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.destinationClicked({
                                ...defaultProperties,
                                block: 'where_to_go',
                                card_number: 0,
                                destination_id: selectedItem.item.id,
                                destination_slug: selectedItem.item.slug,
                            });
                        });
                    }}
                />
            ) : selectedRegion ? (
                <WemapDrawer
                    title={selectedItem.item.name}
                    description={selectedItem.item.description}
                    averageRating={null}
                    picture={selectedItem.item.coverPicture}
                    cta={{
                        label: formatMessage({
                            id: 'global.cta.discoverRegion',
                        }),
                        url: generateUrl('region', {
                            destinationSlug: selectedItem.item.destination.slug,
                            regionSlug: selectedItem.item.slug,
                        }),
                    }}
                    onClose={handleClose}
                    onClickCta={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.regionClicked({
                                ...defaultProperties,
                                block: 'where_to_go',
                                click_type: 'button',
                                card_number: 0,
                                destination_id: selectedItem.item.destination.id,
                                destination_slug: selectedItem.item.destination.slug,
                                region_id: selectedItem.item.id,
                                region_slug: selectedItem.item.slug,
                            });
                        });
                    }}
                />
            ) : selectedPointOfInterest ? (
                <WemapDrawer
                    title={selectedItem.item.name}
                    description={selectedItem.item.description}
                    averageRating={selectedItem.item.averageRating}
                    picture={selectedItem.item.picture}
                    cta={{
                        label: formatMessage({
                            id: 'global.cta.discoverPointOfInterest',
                        }),
                        url: generateUrl('place', {
                            destinationSlug: selectedItem.item.destination.slug,
                            placeId: selectedItem.item.id,
                            placeSlug: selectedItem.item.slug,
                        }),
                    }}
                    onClose={handleClose}
                    onClickCta={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.poiClicked({
                                ...defaultProperties,
                                block: 'where_to_go',
                                card_number: 0,
                                click_type: 'button',
                            });
                        });
                    }}
                />
            ) : null}
        </>
    );
}

WhereToGoSectionMapDrawer.WIDTH = WemapDrawer.WIDTH;
